import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Popover,
  FormControl,
  InputLabel,
  Select,
  alpha,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Send as SendIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  WarningAmber as WarningIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  ThumbUpAltOutlined as ApproveIcon,
  AttachFile as AttachFileIcon,
  ArrowUpward as ArrowUpwardIcon,
  DataObject as DataObjectIcon,
} from '@mui/icons-material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { formatAmount, formatDateOnly, initializeFormats, getFormatConfig } from '../../../utils/global';
import { useToast } from '../../hooks/useToast';
import { useUserRights } from '../../../context/UserRightsContext';
import { useApHub } from '../ApHub/ApHubContext';
import ApBusinessUnitSelector from '../ApHub/ApBusinessUnitSelector';
import InvoicePreviewDrawer from '../Invoices/InvoicePreviewDrawer';
import InvoiceManualEntryDialog from '../Invoices/InvoiceManualEntryDialog';
import InvoiceContentDialog from '../Invoices/InvoiceContentDialog';
import { getLastApprovalFailureReason } from '../Invoices/invoiceReviewUtils';

import {
  AP_HUB_BU_STORAGE_KEY,
  clearDashboardDateRange,
  setDashboardDateRange,
} from '../ApHub/apHubStorage';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateDashboardPageSx,
  harmonateDashboardTitleSx,
  harmonateKpiCardSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonateSectionHeaderSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateLinkButtonSx,
  harmonateLinkSx,
  harmonateToolbarDateFieldSx,
  harmonateToolbarButtonSx,
  harmonateToolbarSx,
  harmonatePlainIconButtonSx,
  harmonateGridActionIconSx,
} from '../../../theme/harmonateStyles';

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const getTenantId = () => localStorage.getItem('tenant_id') || '';
const getUserId = () => localStorage.getItem('user_id') || null;
const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch { /* ignore */ }
    throw new Error(message);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

const deriveDisplayStatus = (row) => {
  const paymentStatus = (row.paymentStatus || '').toUpperCase();
  const workflowStatus = (row.workflowStatus || '').toUpperCase();
  if (paymentStatus === 'PAID') return 'Paid';
  if (workflowStatus === 'APPROVED') return 'Approved';
  if (workflowStatus === 'PENDING_APPROVAL') return 'Pending Approval';
  if (workflowStatus === 'REJECTED') return 'Rejected';
  if (workflowStatus === 'DRAFT') return 'Draft';
  return workflowStatus || paymentStatus || 'Draft';
};

const statusChipProps = (status) => {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'paid') return { color: 'info', variant: 'filled' };
  if (normalized === 'approved') return { color: 'success', variant: 'filled' };
  if (normalized === 'pending approval') return { color: 'warning', variant: 'filled' };
  if (normalized === 'rejected') return { color: 'error', variant: 'outlined' };
  if (normalized === 'draft') return { color: 'default', variant: 'outlined', sx: { bgcolor: '#f5f5f5' } };
  return { color: 'default', variant: 'outlined' };
};

const paymentStatusChipProps = (status) => {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'settled') return { color: 'success', variant: 'filled' };
  if (normalized === 'accepted') return { color: 'info', variant: 'filled' };
  if (normalized === 'submitted') return { color: 'primary', variant: 'filled' };
  if (normalized === 'scheduled') return { color: 'default', variant: 'outlined', sx: { bgcolor: '#e3f2fd', color: '#1565c0' } };
  return { color: 'default', variant: 'outlined' };
};

const mapBatchStatusLabel = (status) => {
  const normalized = (status || '').toUpperCase();
  if (normalized === 'SETTLED') return 'Settled';
  if (normalized === 'ACCEPTED') return 'Accepted';
  if (normalized === 'SUBMITTED') return 'Submitted';
  if (normalized === 'SCHEDULED') return 'Scheduled';
  if (normalized === 'DRAFT') return 'Scheduled';
  return status || 'Scheduled';
};

const isOpenPayable = (inv) => {
  const paymentStatus = (inv.paymentStatus || '').toUpperCase();
  return paymentStatus !== 'PAID';
};

const invoiceAmount = (inv) => {
  const remaining = inv.invoiceRemainingBalanceDue;
  if (remaining != null && Number(remaining) >= 0) return Number(remaining);
  const total = Number(inv.invoiceTotalAmountDue) || 0;
  const paid = Number(inv.invoicePaymentsReceived) || 0;
  return Math.max(0, total - paid);
};

const inDateRange = (value, from, to) => {
  if (!value) return true;
  if (from && value < from) return false;
  if (to && value > to) return false;
  return true;
};

const getMonthRange = (date = new Date()) => {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return {
    from: start.toISOString().slice(0, 10),
    to: end.toISOString().slice(0, 10),
  };
};

const formatTrend = (current, previous) => {
  if (previous === 0 && current === 0) return { text: 'No change from last month', positive: true };
  if (previous === 0) return { text: 'New this month', positive: true };
  const pct = ((current - previous) / previous) * 100;
  return {
    text: `${Math.abs(pct).toFixed(1)}% from last month`,
    positive: pct >= 0,
  };
};

const KpiCard = ({ title, value, subtitle, trend, icon, color, loading }) => (
  <Paper elevation={0} sx={harmonateKpiCardSx}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(color, 0.12),
          color,
        }}
      >
        {icon}
      </Box>
    </Box>
    {loading ? (
      <CircularProgress size={22} />
    ) : (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, fontSize: '1.15rem' }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
            {subtitle}
          </Typography>
        )}
        {trend && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ArrowUpwardIcon
              sx={{
                fontSize: 14,
                color: trend.positive ? 'success.main' : 'error.main',
                transform: trend.positive ? 'none' : 'rotate(180deg)',
              }}
            />
            <Typography variant="caption" color={trend.positive ? 'success.main' : 'error.main'}>
              {trend.text}
            </Typography>
          </Box>
        )}
      </>
    )}
  </Paper>
);

const defaultFilters = {
  vendorId: '',
  status: '',
};

const ApTreasuryDashboard = () => {
  const tenantId = getTenantId();
  const navigate = useNavigate();
  const config = getFormatConfig();
  const { showInfo, showError, showSuccess } = useToast();
  const { hasPermission } = useUserRights();
  const { renderModuleMenu, selectedBusinessUnitId, selectedBusinessUnitName, businessUnits, setSelectedBusinessUnitId } = useApHub();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [paymentBatches, setPaymentBatches] = useState([]);
  const [paymentTransactions, setPaymentTransactions] = useState([]);
  const [reconExceptions, setReconExceptions] = useState([]);
  const [banks, setBanks] = useState([]);
  const [vendors, setVendors] = useState([]);

  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [filters, setFilters] = useState(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
  const [filterAnchor, setFilterAnchor] = useState(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewInvoiceId, setPreviewInvoiceId] = useState(null);
  const [previewInitialTab, setPreviewInitialTab] = useState(0);

  const [editOpen, setEditOpen] = useState(false);
  const [editInvoiceId, setEditInvoiceId] = useState(null);
  const [contentDialogInvoice, setContentDialogInvoice] = useState(null);

  const [rowMenuAnchor, setRowMenuAnchor] = useState(null);
  const [rowMenuInvoice, setRowMenuInvoice] = useState(null);
  const [filterResetKey, setFilterResetKey] = useState(0);

  const vendorMap = useMemo(
    () => Object.fromEntries(vendors.map((v) => [v.vendorId, v.vendorName])),
    [vendors]
  );
  const businessUnitMap = useMemo(
    () => Object.fromEntries(businessUnits.map((bu) => [bu.businessUnitId, bu.businessUnitName])),
    [businessUnits]
  );

  const hasDashboardFilters = Boolean(
    selectedBusinessUnitId || dateRange.from || dateRange.to
  );

  const userScopedInvoices = useMemo(() => {
    const allowed = new Set(businessUnits.map((bu) => bu.businessUnitId));
    return invoices.filter((inv) => inv.businessUnitId && allowed.has(inv.businessUnitId));
  }, [invoices, businessUnits]);

  const filteredInvoices = useMemo(
    () => {
      if (!hasDashboardFilters) return [];
      return userScopedInvoices.filter((inv) => {
      if (selectedBusinessUnitId && inv.businessUnitId !== selectedBusinessUnitId) return false;
      if (appliedFilters.vendorId && inv.vendorId !== appliedFilters.vendorId) return false;
      if (appliedFilters.status && deriveDisplayStatus(inv).toLowerCase() !== appliedFilters.status.toLowerCase()) {
        return false;
      }
      return inDateRange(inv.invoiceDate, dateRange.from, dateRange.to);
    });
    },
    [userScopedInvoices, appliedFilters, dateRange, selectedBusinessUnitId, hasDashboardFilters]
  );

  const kpis = useMemo(() => {
    const openInvoices = filteredInvoices.filter(isOpenPayable);
    const outstandingTotal = openInvoices.reduce((sum, inv) => sum + invoiceAmount(inv), 0);

    const approvedInvoices = filteredInvoices.filter(
      (inv) => (inv.workflowStatus || '').toUpperCase() === 'APPROVED' && isOpenPayable(inv)
    );
    const approvedTotal = approvedInvoices.reduce((sum, inv) => sum + invoiceAmount(inv), 0);

    const submittedStatuses = new Set(['SUBMITTED', 'ACCEPTED', 'PENDING']);
    const submittedTx = paymentTransactions.filter(
      (tx) => submittedStatuses.has((tx.paymentStatus || '').toUpperCase())
        && inDateRange(tx.createdAt?.slice(0, 10), dateRange.from, dateRange.to)
    );
    const submittedTotal = submittedTx.reduce((sum, tx) => sum + (Number(tx.paymentAmount) || 0), 0);

    const scheduledPayments = paymentBatches
      .filter((b) => inDateRange(b.paymentDate, dateRange.from, dateRange.to))
      .reduce((sum, b) => sum + (Number(b.totalAmount) || 0), 0);

    const cashForecast = null;

    const now = new Date();
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().slice(0, 10);
    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().slice(0, 10);

    const thisMonthOutstanding = userScopedInvoices
      .filter((inv) => isOpenPayable(inv) && inv.invoiceDate >= thisMonthStart)
      .reduce((sum, inv) => sum + invoiceAmount(inv), 0);
    const lastMonthOutstanding = userScopedInvoices
      .filter((inv) => isOpenPayable(inv) && inv.invoiceDate >= lastMonthStart && inv.invoiceDate <= lastMonthEnd)
      .reduce((sum, inv) => sum + invoiceAmount(inv), 0);

    return {
      outstanding: {
        amount: outstandingTotal,
        count: openInvoices.length,
        trend: formatTrend(thisMonthOutstanding, lastMonthOutstanding),
      },
      approved: {
        amount: approvedTotal,
        count: approvedInvoices.length,
        trend: formatTrend(approvedInvoices.length, 0),
      },
      submitted: {
        amount: submittedTotal,
        count: submittedTx.length,
        trend: formatTrend(submittedTotal, 0),
      },
      cash: {
        amount: null,
        count: banks.length,
        trend: null,
      },
      forecast: {
        amount: cashForecast,
        subtitle: scheduledPayments > 0 ? `${formatAmount(scheduledPayments, config.AmountFormat)} scheduled` : 'Projected balance',
        trend: null,
      },
      exceptions: {
        count: reconExceptions.length,
        trend: formatTrend(reconExceptions.length, Math.max(0, reconExceptions.length - 2)),
      },
    };
  }, [filteredInvoices, paymentTransactions, paymentBatches, reconExceptions, banks, userScopedInvoices, config.AmountFormat]);

  const recentInvoiceRows = useMemo(
    () => [...filteredInvoices]
      .sort((a, b) => (b.invoiceDate || '').localeCompare(a.invoiceDate || ''))
      .slice(0, 5)
      .map((inv) => {
        const approvalFailureReason = getLastApprovalFailureReason(inv);
        return {
          id: inv.apInvoiceId,
          apInvoiceId: inv.apInvoiceId,
          invoiceNumber: inv.invoiceNumber,
          vendorName: inv.vendorName || vendorMap[inv.vendorId] || '',
          businessUnitName: businessUnitMap[inv.businessUnitId] || '',
          invoiceDate: inv.invoiceDate,
          invoiceDueDate: inv.invoiceDueDate,
          amount: inv.invoiceTotalAmountDue,
          displayStatus: deriveDisplayStatus(inv),
          approvalFailureReason,
          raw: inv,
        };
      }),
    [filteredInvoices, vendorMap, businessUnitMap]
  );

  const vendorNameById = useMemo(() => vendorMap, [vendorMap]);

  const upcomingPaymentRows = useMemo(() => {
    if (!hasDashboardFilters) return [];

    const txByBatch = paymentTransactions.reduce((acc, tx) => {
      const batchId = tx.paymentBatchId;
      if (!batchId) return acc;
      if (!acc[batchId]) acc[batchId] = [];
      acc[batchId].push(tx);
      return acc;
    }, {});

    return [...paymentBatches]
      .filter((b) => {
        const status = (b.batchStatus || '').toUpperCase();
        if (status === 'SETTLED' || status === 'CANCELLED') return false;
        if (selectedBusinessUnitId && b.businessUnitId !== selectedBusinessUnitId) return false;
        return inDateRange(b.paymentDate, dateRange.from, dateRange.to);
      })
      .sort((a, b) => (a.paymentDate || '').localeCompare(b.paymentDate || ''))
      .slice(0, 5)
      .map((batch) => {
        const batchTx = txByBatch[batch.paymentBatchId] || [];
        const firstVendorId = batchTx[0]?.vendorId;
        return {
          id: batch.paymentBatchId,
          paymentDate: batch.paymentDate,
          vendorName: vendorNameById[firstVendorId] || batch.batchNumber || '—',
          amount: batch.totalAmount,
          paymentStatus: mapBatchStatusLabel(batch.batchStatus),
        };
      });
  }, [paymentBatches, paymentTransactions, vendorNameById, selectedBusinessUnitId, dateRange, hasDashboardFilters]);

  const fetchData = useCallback(async () => {
    if (!tenantId || !hasDashboardFilters) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const [
        invoiceData,
        batchData,
        txData,
        vendorData,
        bankData,
        reconData,
      ] = await Promise.all([
        apiFetch(`conductor/ap-invoice-headers/tenant/${tenantId}`),
        apiFetch(`conductor/payment-batches/tenant/${tenantId}`).catch(() => []),
        apiFetch(`conductor/payment-transactions/tenant/${tenantId}`).catch(() => []),
        apiFetch(`conductor/vendor-masters/tenant/${tenantId}`).catch(() => []),
        apiFetch(`conductor/cash-recon/banks/by-tenant?tenant_id=${tenantId}`).catch(() => []),
        apiFetch(`conductor/cash-recon/bank-statements/by-recon-status?tenant_id=${tenantId}&recon_status=UNMATCHED`).catch(() => []),
      ]);

      setInvoices(Array.isArray(invoiceData) ? invoiceData : []);
      setPaymentBatches(Array.isArray(batchData) ? batchData : []);
      setPaymentTransactions(Array.isArray(txData) ? txData : []);
      setVendors(Array.isArray(vendorData) ? vendorData : []);
      setBanks(Array.isArray(bankData) ? bankData : []);
      setReconExceptions(Array.isArray(reconData) ? reconData : []);
    } catch (err) {
      setError(err.message || 'Failed to load dashboard data');
      showError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [tenantId, hasDashboardFilters, showError]);

  const handleRefresh = useCallback(() => {
    setDateRange({ from: '', to: '' });
    setSelectedBusinessUnitId('');
    sessionStorage.removeItem(AP_HUB_BU_STORAGE_KEY);
    clearDashboardDateRange();
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    setInvoices([]);
    setPaymentBatches([]);
    setPaymentTransactions([]);
    setReconExceptions([]);
    setBanks([]);
    setVendors([]);
    setError(null);
    setLoading(false);
    setFilterResetKey((key) => key + 1);
  }, [setSelectedBusinessUnitId]);

  useEffect(() => {
    if (!tenantId || !hasDashboardFilters) return;
    initializeFormats(tenantId);
    fetchData();
  }, [tenantId, hasDashboardFilters, fetchData]);

  useEffect(() => {
    setDashboardDateRange(dateRange);
  }, [dateRange]);

  const openPreview = (invoiceId, tab = 0) => {
    setPreviewInvoiceId(invoiceId);
    setPreviewInitialTab(tab);
    setPreviewOpen(true);
  };

  const closeRowMenu = () => {
    setRowMenuAnchor(null);
    setRowMenuInvoice(null);
  };

  const handleInvoiceAction = (action, row) => {
    closeRowMenu();
    if (action === 'view') openPreview(row.apInvoiceId, 0);
    if (action === 'edit') {
      setEditInvoiceId(row.apInvoiceId);
      setEditOpen(true);
    }
    if (action === 'content') {
      setContentDialogInvoice(row);
    }
    if (action === 'approve') openPreview(row.apInvoiceId, 1);
    if (action === 'attachments') showInfo('Attachments open from the invoice preview.');
  };

  const invoiceColumns = useMemo(() => [
    {
      field: 'invoiceNumber',
      headerName: 'Invoice Number',
      flex: 1,
      minWidth: 130,
      renderCell: (params) => (
        <Typography
          component="span"
          sx={harmonateLinkSx}
          onClick={() => openPreview(params.row.apInvoiceId, 0)}
        >
          {params.value}
        </Typography>
      ),
    },
    { field: 'vendorName', headerName: 'Vendor', flex: 1, minWidth: 120 },
    { field: 'businessUnitName', headerName: 'Business Unit', flex: 1, minWidth: 120 },
    {
      field: 'invoiceDate',
      headerName: 'Invoice Date',
      width: 115,
      valueFormatter: (p) => (p.value ? formatDateOnly(p.value, config.DateFormat) : ''),
    },
    {
      field: 'invoiceDueDate',
      headerName: 'Due Date',
      width: 105,
      valueFormatter: (p) => (p.value ? formatDateOnly(p.value, config.DateFormat) : ''),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 110,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (p) => (p.value != null ? formatAmount(p.value, config.AmountFormat) : ''),
    },
    {
      field: 'displayStatus',
      headerName: 'Status',
      width: 140,
      renderCell: (params) => {
        const chip = statusChipProps(params.value);
        const failureReason = params.row.approvalFailureReason;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Chip label={params.value} size="small" color={chip.color} variant={chip.variant} sx={chip.sx} />
            {failureReason ? (
              <Tooltip title={failureReason}>
                <WarningIcon sx={{ color: '#d32f2f', fontSize: 18 }} />
              </Tooltip>
            ) : null}
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 48,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setRowMenuAnchor(e.currentTarget);
            setRowMenuInvoice(params.row);
          }}
          sx={harmonateGridActionIconSx}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ], [config.AmountFormat, config.DateFormat]);

  const paymentColumns = useMemo(() => [
    {
      field: 'paymentDate',
      headerName: 'Payment Date',
      flex: 1,
      minWidth: 120,
      valueFormatter: (p) => (p.value ? formatDateOnly(p.value, config.DateFormat) : ''),
    },
    { field: 'vendorName', headerName: 'Vendor', flex: 1, minWidth: 140 },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (p) => (p.value != null ? formatAmount(p.value, config.AmountFormat) : ''),
    },
    {
      field: 'paymentStatus',
      headerName: 'Payment Status',
      width: 120,
      renderCell: (params) => {
        const chip = paymentStatusChipProps(params.value);
        return <Chip label={params.value} size="small" color={chip.color} variant={chip.variant} sx={chip.sx} />;
      },
    },
  ], [config.AmountFormat, config.DateFormat]);

  const kpiCards = [
    {
      key: 'outstanding',
      title: 'Outstanding Payables',
      value: formatAmount(kpis.outstanding.amount, config.AmountFormat),
      subtitle: `${kpis.outstanding.count} Invoices`,
      trend: kpis.outstanding.trend,
      icon: <DescriptionIcon fontSize="small" />,
      color: ht.text,
    },
    {
      key: 'approved',
      title: 'Approved for Payment',
      value: formatAmount(kpis.approved.amount, config.AmountFormat),
      subtitle: `${kpis.approved.count} Invoices`,
      trend: kpis.approved.trend,
      icon: <CheckCircleIcon fontSize="small" />,
      color: ht.green,
    },
    {
      key: 'submitted',
      title: 'Payments Submitted',
      value: formatAmount(kpis.submitted.amount, config.AmountFormat),
      subtitle: `${kpis.submitted.count} Payments`,
      trend: kpis.submitted.trend,
      icon: <SendIcon fontSize="small" />,
      color: ht.text,
    },
    {
      key: 'cash',
      title: 'Available Cash Balance',
      value: kpis.cash.amount != null ? formatAmount(kpis.cash.amount, config.AmountFormat) : '—',
      subtitle: banks.length ? `Across ${banks.length} Bank Accounts` : 'No bank accounts linked',
      trend: null,
      icon: <AccountBalanceIcon fontSize="small" />,
      color: ht.text,
    },
    {
      key: 'forecast',
      title: 'Cash Forecast (30 Days)',
      value: kpis.forecast.amount != null ? formatAmount(kpis.forecast.amount, config.AmountFormat) : '—',
      subtitle: kpis.forecast.subtitle,
      trend: null,
      icon: <TrendingUpIcon fontSize="small" />,
      color: ht.text,
    },
    {
      key: 'exceptions',
      title: 'Reconciliation Exceptions',
      value: String(kpis.exceptions.count),
      subtitle: 'Total Exceptions',
      trend: kpis.exceptions.trend,
      icon: <WarningIcon fontSize="small" />,
      color: ht.red,
    },
  ];

  return (
    <Box sx={harmonateDashboardPageSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 2, flexWrap: 'wrap', flexShrink: 0 }}>
        <Box>
          <Typography sx={harmonateDashboardTitleSx}>
            Payment Intelligence
          </Typography>
        </Box>

        <Box sx={harmonateToolbarSx}>
          <Typography variant="body2" sx={{ color: ht.white, fontWeight: 600, whiteSpace: 'nowrap' }}>
            Invoice Date
          </Typography>
          <TextField
            size="small"
            type="date"
            label="From"
            value={dateRange.from}
            onChange={(e) => setDateRange((prev) => ({ ...prev, from: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={harmonateToolbarDateFieldSx}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            value={dateRange.to}
            onChange={(e) => setDateRange((prev) => ({ ...prev, to: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={harmonateToolbarDateFieldSx}
          />
          <Button
            variant="outlined"
            startIcon={<CalendarIcon />}
            onClick={() => setDateRange(getMonthRange())}
            sx={harmonateToolbarButtonSx}
          >
            This Month
          </Button>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={(e) => setFilterAnchor(e.currentTarget)}
            sx={harmonateToolbarButtonSx}
          >
            Filters
          </Button>
          <ApBusinessUnitSelector key={filterResetKey} variant="harmonate-toolbar" />
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} size="small" aria-label="Refresh dashboard" sx={harmonatePlainIconButtonSx}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {renderModuleMenu()}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2, flexShrink: 0 }}>
        {kpiCards.map((card) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={card.key}>
            <KpiCard {...card} loading={loading && hasDashboardFilters} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ flex: 1, minHeight: 0, alignItems: 'stretch' }}>
        <Grid item xs={12} lg={7} sx={{ display: 'flex', minHeight: 0 }}>
          <Paper elevation={0} sx={{ ...harmonateCardSx, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ ...harmonateSectionHeaderSx, flexShrink: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: ht.text }}>
                Recent Invoices
              </Typography>
              <Button
                size="small"
                onClick={() => navigate('/treasury-payment/invoices')}
                sx={{ ...harmonateLinkSx, fontSize: '0.875rem' }}
              >
                View All
              </Button>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              {!hasDashboardFilters ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', px: 2 }}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Select a business unit and/or date range to view recent invoices.
                  </Typography>
                </Box>
              ) : loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <DataGridPro
                  rows={recentInvoiceRows}
                  columns={invoiceColumns}
                  rowHeight={44}
                  hideFooterSelectedRowCount
                  disableRowSelectionOnClick
                  autoHeight={false}
                  pageSizeOptions={[5]}
                  initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
                  getRowClassName={(params) => (params.row.approvalFailureReason ? 'approval-failed-row' : '')}
                  sx={{
                    ...harmonateDataGridSx,
                    height: '100%',
                    '& .approval-failed-row': { bgcolor: alpha('#d32f2f', 0.04) },
                  }}
                />
              )}
            </Box>
            <Box sx={{ px: 2, py: 1, borderTop: `1px solid ${ht.line}`, bgcolor: ht.white, flexShrink: 0 }}>
              <Typography variant="caption" color="text.secondary">
                {hasDashboardFilters
                  ? `Showing 1 to ${recentInvoiceRows.length} of ${filteredInvoices.length} invoices`
                  : 'No invoices to display'}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5} sx={{ display: 'flex', minHeight: 0 }}>
          <Paper elevation={0} sx={{ ...harmonateCardSx, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ ...harmonateSectionHeaderSx, flexShrink: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: ht.text }}>
                Upcoming Payments
              </Typography>
              <Button
                size="small"
                onClick={() => navigate('/treasury-payment/payment-proposal')}
                sx={{ ...harmonateLinkSx, fontSize: '0.875rem' }}
              >
                View All
              </Button>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              {!hasDashboardFilters ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', px: 2 }}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Select a business unit and/or date range to view upcoming payments.
                  </Typography>
                </Box>
              ) : loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <DataGridPro
                  rows={upcomingPaymentRows}
                  columns={paymentColumns}
                  rowHeight={44}
                  hideFooterSelectedRowCount
                  disableRowSelectionOnClick
                  autoHeight={false}
                  pageSizeOptions={[5]}
                  initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
                  sx={{ ...harmonateDataGridSx, height: '100%' }}
                />
              )}
            </Box>
            <Box sx={{ px: 2, py: 1, borderTop: `1px solid ${ht.line}`, bgcolor: ht.white, flexShrink: 0 }}>
              <Typography variant="caption" color="text.secondary">
                {hasDashboardFilters
                  ? `Showing 1 to ${upcomingPaymentRows.length} of ${upcomingPaymentRows.length} payments`
                  : 'No payments to display'}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Popover
        open={Boolean(filterAnchor)}
        anchorEl={filterAnchor}
        onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, width: 280, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Vendor</InputLabel>
            <Select
              label="Vendor"
              value={filters.vendorId}
              onChange={(e) => setFilters((prev) => ({ ...prev, vendorId: e.target.value }))}
            >
              <MenuItem value="">All</MenuItem>
              {vendors.map((v) => (
                <MenuItem key={v.vendorId} value={v.vendorId}>
                  {v.vendorName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={filters.status}
              onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Draft">Draft</MenuItem>
              <MenuItem value="Approved">Approved</MenuItem>
              <MenuItem value="Paid">Paid</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              size="small"
              onClick={() => {
                setFilters(defaultFilters);
                setAppliedFilters(defaultFilters);
                setFilterAnchor(null);
              }}
              sx={harmonateCancelButtonSx}
            >
              Reset
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                setAppliedFilters({ ...filters });
                setFilterAnchor(null);
              }}
              sx={harmonateProceedButtonSx}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>

      <Menu anchorEl={rowMenuAnchor} open={Boolean(rowMenuAnchor)} onClose={closeRowMenu}>
        <MenuItem onClick={() => handleInvoiceAction('view', rowMenuInvoice)}>
          <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Invoice</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleInvoiceAction('content', rowMenuInvoice)}>
          <ListItemIcon><DataObjectIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Content</ListItemText>
        </MenuItem>
      </Menu>

      <InvoicePreviewDrawer
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        invoiceId={previewInvoiceId}
        tenantId={tenantId}
        initialTab={previewInitialTab}
        onConfirmed={fetchData}
        showSuccess={showSuccess}
        showError={showError}
      />

      <InvoiceManualEntryDialog
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setEditInvoiceId(null);
        }}
        tenantId={tenantId}
        vendors={vendors}
        businessUnits={businessUnits}
        businessUnitId={selectedBusinessUnitId}
        businessUnitName={selectedBusinessUnitName}
        invoiceId={editInvoiceId}
        onSaved={fetchData}
        showSuccess={showSuccess}
        showError={showError}
      />

      <InvoiceContentDialog
        open={Boolean(contentDialogInvoice)}
        onClose={() => setContentDialogInvoice(null)}
        invoiceId={contentDialogInvoice?.apInvoiceId}
        invoiceNumber={contentDialogInvoice?.invoiceNumber}
      />
    </Box>
  );
};

export default ApTreasuryDashboard;
